/**
 * Utility functions for analyzing word duplication in keyword selections
 */

// Common stop words to ignore in duplication analysis
const STOP_WORDS = new Set([
  'a', 'an', 'and', 'are', 'as', 'at', 'be', 'by', 'for', 'from',
  'has', 'he', 'in', 'is', 'it', 'its', 'of', 'on', 'that', 'the',
  'to', 'was', 'were', 'will', 'with', 'the', 'this', 'but', 'they',
  'have', 'had', 'what', 'said', 'each', 'which', 'she', 'do', 'how',
  'their', 'if', 'up', 'out', 'many', 'then', 'them', 'these', 'so',
  'some', 'her', 'would', 'make', 'like', 'into', 'him', 'time', 'has',
  'look', 'two', 'more', 'write', 'go', 'see', 'number', 'no', 'way',
  'could', 'people', 'my', 'than', 'first', 'water', 'been', 'call',
  'who', 'oil', 'sit', 'now', 'find', 'down', 'day', 'did', 'get',
  'come', 'made', 'may', 'part'
]);

// Simple stemming: convert to base form (singular) for counting, but we'll track preferred form (plural)
function getStem(word: string): string {
  // Convert to lowercase
  let normalized = word.toLowerCase().trim();
  
  // Remove punctuation
  normalized = normalized.replace(/[^\w\s]/g, '');
  
  // If empty after cleaning, return empty
  if (!normalized) return '';
  
  // Simple stemming: remove trailing 's' to get base form for counting
  // "bars" -> "bar", "bar" -> "bar" (same word)
  // "boxes" -> "box", "box" -> "box"
  // "chocolate" -> "chocolate" (no change)
  if (normalized.length > 2 && normalized.endsWith('s') && !normalized.endsWith('ss')) {
    // Remove trailing 's' but handle special cases
    if (normalized.endsWith('ies')) {
      // "berries" -> "berry"
      return normalized.slice(0, -3) + 'y';
    } else if (normalized.endsWith('es') && normalized.length > 3) {
      // "boxes" -> "box", but not "yes"
      return normalized.slice(0, -2);
    } else {
      // "bars" -> "bar"
      return normalized.slice(0, -1);
    }
  }
  
  return normalized;
}

/**
 * Extracts individual words from a keyword phrase
 * Returns a map of stem -> preferred form (prefer plural)
 */
function extractWords(keyword: string): Map<string, string> {
  const words = new Map<string, string>();
  const tokens = keyword.toLowerCase().split(/\s+/);
  
  tokens.forEach(token => {
    // Remove punctuation and clean
    const cleaned = token.replace(/[^\w]/g, '');
    if (!cleaned || cleaned.length < 2) return; // Skip very short tokens
    
    if (STOP_WORDS.has(cleaned)) return;
    
    // Get stem for counting (normalize to singular/base form)
    const stem = getStem(cleaned);
    if (!stem) return;
    
    // Store the stem -> preferred form (prefer plural if we've seen it)
    if (!words.has(stem)) {
      words.set(stem, cleaned);
    } else {
      // Prefer plural form: if existing is singular and current is plural, update
      const existing = words.get(stem)!;
      if (!existing.endsWith('s') && cleaned.endsWith('s')) {
        // Update to plural
        words.set(stem, cleaned);
      }
      // If both are plural or both are singular, keep existing (first seen)
    }
  });
  
  return words;
}

/**
 * Detects exact phrase duplication (currently unused but kept for potential future use)
 */
// function extractPhrases(keywords: string[]): string[] {
//   return keywords.map(k => k.toLowerCase().trim());
// }

export interface DuplicationAlert {
  word: string;
  frequency: number;
  percentage: number;
  keywords: string[]; // Keywords containing this word
}

export interface ProductDuplicationAnalysis {
  productId: string;
  productName: string;
  wordAlerts: DuplicationAlert[]; // Words exceeding threshold
  phraseAlerts: string[]; // Exact duplicate phrases
  totalKeywords: number;
}

export interface PortfolioDuplicationAnalysis {
  wordAlerts: DuplicationAlert[]; // Words exceeding threshold across portfolio
  phraseAlerts: string[]; // Exact duplicate phrases across portfolio
  totalKeywords: number;
}

/**
 * Analyze word duplication within a single product's selected keywords
 */
export function analyzeProductDuplication(
  productId: string,
  productName: string,
  selectedKeywords: string[],
  threshold: number = 0.4
): ProductDuplicationAnalysis {
  if (selectedKeywords.length === 0) {
    return {
      productId,
      productName,
      wordAlerts: [],
      phraseAlerts: [],
      totalKeywords: 0
    };
  }

  // Track word frequency by stem, but also track preferred form (plural)
  const wordFrequency = new Map<string, { count: number; keywords: Set<string>; preferredForm: string }>();
  
  // Track phrase frequency
  const phraseFrequency = new Map<string, number>();
  
  selectedKeywords.forEach(keyword => {
    // Extract words from this keyword
    // extractWords returns Map<stem, preferredForm>
    const words = extractWords(keyword);
    words.forEach((preferredForm, stem) => {
      if (!wordFrequency.has(stem)) {
        wordFrequency.set(stem, { count: 0, keywords: new Set(), preferredForm });
      }
      const entry = wordFrequency.get(stem)!;
      entry.count++;
      entry.keywords.add(keyword);
      // Update preferred form if we see a plural version
      if (preferredForm.endsWith('s') && !entry.preferredForm.endsWith('s')) {
        entry.preferredForm = preferredForm;
      }
    });
    
    // Track exact phrases
    const phrase = keyword.toLowerCase().trim();
    phraseFrequency.set(phrase, (phraseFrequency.get(phrase) || 0) + 1);
  });
  
  // Find words exceeding threshold
  // Only check for alerts if we have at least 2 keywords (otherwise percentages aren't meaningful)
  const wordAlerts: DuplicationAlert[] = [];
  const totalKeywords = selectedKeywords.length;
  
  if (totalKeywords >= 2) {
    wordFrequency.forEach((data, stem) => {
      const percentage = data.count / totalKeywords;
      if (percentage > threshold) {
        wordAlerts.push({
          word: data.preferredForm, // Display preferred form (plural if available)
          frequency: data.count,
          percentage,
          keywords: Array.from(data.keywords)
        });
      }
    });
  }
  
  // Find duplicate phrases (only meaningful if we have at least 2 keywords)
  const phraseAlerts: string[] = [];
  if (totalKeywords >= 2) {
    phraseFrequency.forEach((count, phrase) => {
      if (count > 1) {
        phraseAlerts.push(phrase);
      }
    });
  }
  
  // Sort by percentage descending
  wordAlerts.sort((a, b) => b.percentage - a.percentage);
  
  return {
    productId,
    productName,
    wordAlerts,
    phraseAlerts,
    totalKeywords
  };
}

/**
 * Analyze word duplication across the entire portfolio
 */
export function analyzePortfolioDuplication(
  products: Array<{ id: string; name: string; selectedKeywords: string[] }>,
  threshold: number = 0.4
): PortfolioDuplicationAnalysis {
  // Collect all selected keywords across all products
  const allKeywords: string[] = [];
  products.forEach(product => {
    allKeywords.push(...product.selectedKeywords);
  });
  
  if (allKeywords.length === 0) {
    return {
      wordAlerts: [],
      phraseAlerts: [],
      totalKeywords: 0
    };
  }
  
  // Track word frequency across portfolio by stem, but also track preferred form (plural)
  const wordFrequency = new Map<string, { count: number; keywords: Set<string>; preferredForm: string }>();
  const phraseFrequency = new Map<string, number>();
  
  allKeywords.forEach(keyword => {
    // Extract words
    // extractWords returns Map<stem, preferredForm>
    const words = extractWords(keyword);
    words.forEach((preferredForm, stem) => {
      if (!wordFrequency.has(stem)) {
        wordFrequency.set(stem, { count: 0, keywords: new Set(), preferredForm });
      }
      const entry = wordFrequency.get(stem)!;
      entry.count++;
      entry.keywords.add(keyword);
      // Update preferred form if we see a plural version
      if (preferredForm.endsWith('s') && !entry.preferredForm.endsWith('s')) {
        entry.preferredForm = preferredForm;
      }
    });
    
    // Track exact phrases
    const phrase = keyword.toLowerCase().trim();
    phraseFrequency.set(phrase, (phraseFrequency.get(phrase) || 0) + 1);
  });
  
  // Find words exceeding threshold
  // Only check for alerts if we have at least 2 keywords (otherwise percentages aren't meaningful)
  const wordAlerts: DuplicationAlert[] = [];
  const totalKeywords = allKeywords.length;
  
  if (totalKeywords >= 2) {
    wordFrequency.forEach((data, stem) => {
      const percentage = data.count / totalKeywords;
      if (percentage > threshold) {
        wordAlerts.push({
          word: data.preferredForm, // Display preferred form (plural if available)
          frequency: data.count,
          percentage,
          keywords: Array.from(data.keywords)
        });
      }
    });
  }
  
  // Find duplicate phrases (only meaningful if we have at least 2 keywords)
  const phraseAlerts: string[] = [];
  if (totalKeywords >= 2) {
    phraseFrequency.forEach((count, phrase) => {
      if (count > 1) {
        phraseAlerts.push(phrase);
      }
    });
  }
  
  // Sort by percentage descending
  wordAlerts.sort((a, b) => b.percentage - a.percentage);
  
  return {
    wordAlerts,
    phraseAlerts,
    totalKeywords
  };
}

